<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, useSlots, watch } from 'vue'

import { useScrollLock } from '@composables/useScrollLock'
import { useOverlayFocusTrap } from '@ui/composables/useOverlayFocusTrap'

import { overlayLayerTarget } from '@/utils/overlayLayers'

import AppButton from '@ui/app-button/AppButton.vue'
import AppIconButton from '@ui/app-icon-button/AppIconButton.vue'

import type { AppModalEmits, AppModalProps } from './types'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<AppModalProps>(), {
  title: '',
  text: '',
  negativeButtonText: '',
  positiveButtonText: '',
  close: true,
  header: true,
  footer: true,
  variant: 'default',
  modalClass: '',
  maxWidth: 440,
  layer: 'modals',
  fullHeight: false,
  padding: true,
  autofocus: true,
})

const model = defineModel<boolean>({
  required: true,
})

const emit = defineEmits<AppModalEmits>()

const slots = useSlots()
const titleId = useId()
const bodyId = useId()
const wrapperRef = ref<HTMLElement | null>(null)

const { activate, deactivate, isTopOverlay, stackStyle } = useOverlayFocusTrap(wrapperRef, model, {
  // Escape is handled by `onKeydown` (gated by `close`); overlay clicks must work.
  escapeDeactivates: false,
  allowOutsideClick: true,
  returnFocusOnDeactivate: true,
  fallbackFocus: () => wrapperRef.value as HTMLElement,
  initialFocus: () => (props.autofocus ? undefined : wrapperRef.value),
})

const mods = computed(() => {
  return [
    `variant-${props.variant}`,
    { 'no-padding': !props.padding },
  ]
})

const hasFooter = computed(() => {
  return props.positiveButtonText || props.negativeButtonText || !!(slots.footer)
})

const wrapperWidth = computed(() => {
  return typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth
})

const teleportTarget = computed(() => overlayLayerTarget(props.layer))

const { lock, unlock } = useScrollLock()

function onKeydown(event: KeyboardEvent) {
  if (event.defaultPrevented || !isTopOverlay()) return
  if (event.key === 'Escape' && props.close) {
    event.preventDefault()
    closeModal()
  }
}

watch(
  model,
  async(value) => {
    if (value) {
      lock()
      document.addEventListener('keydown', onKeydown)
      await nextTick()
      if (model.value) activate()
    } else {
      unlock()
      deactivate()
      document.removeEventListener('keydown', onKeydown)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})

function closeModal() {
  model.value = false
  emit('close')
}

function onOverlayClick() {
  if (props.close) closeModal()
}

</script>

<template>
  <teleport :to="teleportTarget">
    <transition
      name="modal-fade"
      appear
      @after-leave="emit('after-leave')"
    >
      <div
        v-if="model"
        v-bind="$attrs"
        class="app-modal"
        :class="mods"
        :style="stackStyle"
        data-test-id="app-modal"
      >
        <div
          class="app-modal-overlay"
          data-test-id="app-modal-overlay"
          @click="onOverlayClick"
        />

        <div
          ref="wrapperRef"
          class="app-modal-wrapper"
          :class="modalClass"
          :style="{
            '--app-modal-max-width': wrapperWidth,
            ...(fullHeight ? { minHeight: '100dvh', maxHeight: '100dvh' } : {}),
          }"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="header ? titleId : undefined"
          :aria-describedby="bodyId"
          tabindex="-1"
          data-test-id="app-modal-wrapper"
        >
          <template v-if="header">
            <div
              class="app-modal-header font-subsection"
              :class="{ 'has-close': close }"
              data-test-id="app-modal-header"
            >
              <slot name="header" :title-id="titleId">
                <h2 :id="titleId" class="app-modal-title font-subsection">
                  {{ title }}
                </h2>
              </slot>

              <app-icon-button
                v-if="close"
                variant="close"
                icon="Close"
                aria-label="Close dialog"
                @click="closeModal"
              />
            </div>
          </template>

          <div
            :id="bodyId"
            class="app-modal-body"
            data-test-id="app-modal-body"
          >
            <slot name="body">
              {{ text }}
            </slot>
          </div>

          <template v-if="footer">
            <div
              v-if="hasFooter"
              class="app-modal-footer"
              data-test-id="app-modal-footer"
            >
              <slot name="footer">
                <app-button
                  v-if="negativeButtonText"
                  variant="secondary"
                  block
                  @click="emit('negative-click')"
                >
                  {{ negativeButtonText }}
                </app-button>

                <app-button
                  v-if="positiveButtonText"
                  variant="accent"
                  block
                  @click="emit('positive-click')"
                >
                  {{ positiveButtonText }}
                </app-button>
              </slot>
            </div>
          </template>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style lang="scss" scoped>
@use '@/styles/breakpoints' as *;

.app-modal {
  --header-padding-left: 0;

  position: fixed;
  z-index: calc(var(--z-layer-modal) + var(--z-stack, 0));
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100dvh;

  &.variant-center {
    text-align: center;

    .app-modal-header.has-close {
      --header-padding-left: 48px;
    }

    .app-modal-footer {
      flex-wrap: wrap;
      justify-content: center;
    }
  }

  &.no-padding &-wrapper {
    padding: 0;
  }

  &-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgb(0 0 0 / 30%);
  }

  &-wrapper {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: var(--app-modal-max-width);
    max-height: 90vh;
    padding: 24px 32px 32px;
    gap: 16px;
    border-radius: 18px;
    background-color: var(--g-neutral-8);

    &:focus {
      outline: none;
    }
  }

  &-header {
    display: grid;
    grid-template-columns: 1fr;
    padding-left: var(--header-padding-left);
    column-gap: 16px;
    color: var(--g-neutral-2);
    font-weight: 500;

    &.has-close {
      grid-template-columns: 1fr auto;
    }
  }

  &-title {
    margin-bottom: 0;
    min-width: 0;
    overflow-wrap: break-word;
  }

  &-body {
    margin: -4px;
    flex: 1;
    min-height: 0;
    padding: 4px;
    overflow-y: auto;
    color: var(--g-neutral-1);
    font-size: 16px;
    font-weight: 500;
  }

  &-footer {
    display: flex;
    align-items: center;
    padding: 16px 0 0;
    gap: 8px;
  }
}

@include tablet-down {
  .app-modal {
    align-items: flex-end;

    &-wrapper {
      max-width: none;
      padding: 16px 16px 24px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &.no-padding &-wrapper {
      padding: 0;
    }
  }
}
</style>
